import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useStripeOnMount } from '@/provider/stripeLoader';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  fetchAddress,
  fetchSubscription,
  fetchUsage,
  fetchPaymentMethods,
  fetchPrices,
} from '@/features/billing/billingThunks';
import {
  checkoutWithNewCard,
  readPaymentMethodId,
  saveDefaultCard,
  type CardSetupResult,
  type SavedCard,
} from '@/features/billing/subscriptionCheckout';
import { useSubmitLock } from '@/hooks/useSubmitLock';
import { cn } from '@/lib/utils';
import { isPriceCurrencyMismatch, resolveCheckoutErrorKey } from '@/lib/subscriptionPaymentResume';
import { toast } from 'react-toastify';
import { fetchWorkspaceById } from '@/features/workspace/workspaceThunks';
import { selectAddress, selectPrices, selectSubscription } from '@/features/billing/billingSlice';
import { ShieldCheck } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from 'react-router-dom';
import { formatPrice, resolvePriceCurrency } from '@/components/billing/usageExpiredUtils';

function StripeInputWithLabel({
  label,
  id,
  StripeElement,
  onCompleteChange,
}: {
  label: string;
  id: string;
  StripeElement: typeof CardNumberElement | typeof CardExpiryElement | typeof CardCvcElement;
  onCompleteChange?: (complete: boolean) => void;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  const isActive = isFocused || hasValue;

  return (
    <div className={cn('relative px-4 py-2 h-14 rounded-lg border border-input bg-white')}>
      <label
        className={cn(
          'absolute transition-all duration-200 pointer-events-none text-tertiary',
          isActive ? 'text-sm top-0.5 left-3 px-1' : 'text-base top-1/2 left-4 -translate-y-1/2',
        )}
        htmlFor={id}
      >
        {label}
      </label>
      <div className="pt-4">
        <StripeElement
          id={id}
          options={{
            placeholder: '',
            style: {
              base: {
                color: '#09090a',
                fontSize: '16px',
                fontWeight: '400',
                padding: '8px 8px 8px 8px',
              },
            },
          }}
          onChange={(event) => {
            setHasValue(!event.empty);
            onCompleteChange?.(event.complete);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </div>
    </div>
  );
}

export default function PaymentForm({
  amount,
  priceId,
  workspaceId,
  termsAccepted,
  onTermsAcceptedChange,
  onSuccess,
  onBeforePayment,
  setupOnly,
  onSetupSuccess,
  promoCode = null,
  disabled = false,
}: {
  amount?: number | null;
  priceId?: string | null;
  workspaceId?: number | null;
  termsAccepted: boolean;
  onTermsAcceptedChange: (accepted: boolean) => void;
  onSuccess?: () => void;
  onBeforePayment?: () => Promise<boolean>;
  setupOnly?: boolean;
  promoCode?: string | null;
  disabled?: boolean;
  onSetupSuccess?: (paymentMethodId: string, termsAccepted: boolean) => void | Promise<void>;
}) {
  const { t } = useTranslation('billing');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  useStripeOnMount();
  const user = useAppSelector((s: RootState) => s.auth.user);
  const address = useAppSelector(selectAddress);
  const subscription = useAppSelector(selectSubscription);
  const prices = useAppSelector(selectPrices);
  const currentWorkspace = useAppSelector((s: RootState) => s.workspace.currentWorkspace);
  const workspaceName = currentWorkspace?.name;
  const currency = resolvePriceCurrency(prices, priceId, currentWorkspace?.billingCurrency);

  useEffect(() => {
    if (workspaceId) dispatch(fetchAddress(workspaceId));
  }, [workspaceId, dispatch]);
  const { isLocked: isProcessing, runExclusive } = useSubmitLock();
  const [isCardNumberComplete, setIsCardNumberComplete] = useState(false);
  const [isCardExpiryComplete, setIsCardExpiryComplete] = useState(false);
  const [isCardCvcComplete, setIsCardCvcComplete] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [isCardholderNameFocused, setIsCardholderNameFocused] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  // Carte déjà enregistrée par un essai précédent dont la souscription a échoué, oubliée dès
  // que la saisie change.
  const [savedPaymentMethodId, setSavedPaymentMethodId] = useState<string | null>(null);

  const isFormValid = isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete;

  const handleCardChange = (setComplete: (complete: boolean) => void) => (complete: boolean) => {
    setComplete(complete);
    setSavedPaymentMethodId(null);
  };

  const handleReset = () => {
    setSavedPaymentMethodId(null);
    setResetKey((k) => k + 1);
    setCardholderName('');
    setIsCardNumberComplete(false);
    setIsCardExpiryComplete(false);
    setIsCardCvcComplete(false);
  };

  const submitPayment = async () => {
    if (!stripe || !elements || !workspaceId || (!setupOnly && !priceId) || !termsAccepted) {
      return;
    }

    try {
      if (onBeforePayment) {
        const ok = await onBeforePayment();
        if (!ok) return;
      }

      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        toast.error(t('paymentForm.toast.formLoadError'));
        return;
      }

      const billingDetails = {
        name:
          cardholderName.trim() ||
          (user ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() : '') ||
          undefined,
        email: user?.email ?? undefined,
      };

      const confirmCardSetup = async (clientSecret: string): Promise<CardSetupResult> => {
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        });
        return {
          error: error ? { message: error.message } : null,
          paymentMethodId: readPaymentMethodId(setupIntent?.payment_method),
        };
      };

      const notifyCardNotSaved = (card: Exclude<SavedCard, { kind: 'saved' }>) => {
        if (card.kind === 'refused') {
          toast.error(card.message ?? t('paymentForm.toast.saveCardError'));
        } else {
          toast.error(t('paymentForm.toast.noPaymentMethod'));
        }
      };

      if (setupOnly) {
        const card = await saveDefaultCard({ dispatch, workspaceId, confirmCardSetup });
        if (card.kind !== 'saved') {
          notifyCardNotSaved(card);
          return;
        }

        await dispatch(fetchPaymentMethods(workspaceId));
        if (onSetupSuccess) {
          await onSetupSuccess(card.paymentMethodId, termsAccepted);
        } else {
          toast.success(t('paymentForm.toast.cardSaved'));
          onSuccess?.();
        }
        return;
      }

      const hasValidAddress =
        address?.companyName &&
        address?.line1 &&
        address?.city &&
        address?.postalCode &&
        address?.country;

      const billingAddress = hasValidAddress
        ? {
            companyName: address!.companyName!,
            line1: address!.line1!,
            line2: address?.line2 ?? undefined,
            city: address!.city!,
            postalCode: address!.postalCode!,
            country: address!.country!,
            taxId: address?.taxId ?? undefined,
          }
        : undefined;

      const checkout = await checkoutWithNewCard({
        dispatch,
        workspaceId,
        confirmCardSetup,
        savedPaymentMethodId,
        onCardSaved: setSavedPaymentMethodId,
        input: {
          priceId: priceId!,
          customerName: address?.companyName ?? workspaceName ?? undefined,
          billingAddress: billingAddress ?? undefined,
          promoCode: promoCode ?? undefined,
          termsAccepted,
        },
        currentStatus: subscription?.workspaceStatus,
        pendingPlan: subscription,
        requestedPlan: prices?.find((price) => price.id === priceId),
        pendingPromoCode: currentWorkspace?.promoCode,
      });

      if (checkout.kind === 'cardNotSaved') {
        notifyCardNotSaved(checkout.card);
        return;
      }

      const { outcome, paymentMethodId } = checkout;
      if (outcome.kind === 'hostedInvoice') {
        window.location.assign(outcome.url);
        return;
      }

      if (outcome.kind === 'confirm') {
        const { error: paymentError } = await stripe.confirmCardPayment(outcome.clientSecret, {
          payment_method: paymentMethodId,
        });
        if (paymentError) {
          toast.error(paymentError.message ?? t('paymentForm.toast.paymentFailed'));
          return;
        }
      }

      await dispatch(fetchSubscription(workspaceId)).unwrap();
      await Promise.all([
        dispatch(fetchUsage(workspaceId)),
        dispatch(fetchPaymentMethods(workspaceId)),
        dispatch(fetchWorkspaceById(workspaceId)),
      ]);
      toast.success(t('paymentForm.toast.subscriptionCreated'));
      onSuccess?.();
    } catch (err: unknown) {
      if (isPriceCurrencyMismatch(err)) {
        void dispatch(fetchWorkspaceById(workspaceId));
        void dispatch(fetchPrices({ workspaceId, ...(promoCode && { promoCode }) }));
      }
      const errorKey = resolveCheckoutErrorKey(err);
      toast.error(errorKey ? tCommon(errorKey) : t('paymentForm.toast.subscriptionError'));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void runExclusive(submitPayment);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div key={resetKey} className="space-y-4">
        <StripeInputWithLabel
          label={t('upgradePayment.cardNumber')}
          id="cardNumber"
          StripeElement={CardNumberElement}
          onCompleteChange={handleCardChange(setIsCardNumberComplete)}
        />

        <div className="flex gap-4">
          <div className="w-1/2">
            <StripeInputWithLabel
              label={t('upgradePayment.expiryDate')}
              id="cardExpiry"
              StripeElement={CardExpiryElement}
              onCompleteChange={handleCardChange(setIsCardExpiryComplete)}
            />
          </div>
          <div className="w-1/2">
            <StripeInputWithLabel
              label={t('upgradePayment.cvv')}
              id="cardCvc"
              StripeElement={CardCvcElement}
              onCompleteChange={handleCardChange(setIsCardCvcComplete)}
            />
          </div>
        </div>

        <div className={cn('relative px-4 py-2 h-14 rounded-lg border border-input bg-white')}>
          <label
            className={cn(
              'absolute transition-all duration-200 pointer-events-none text-tertiary',
              cardholderName.length > 0 || isCardholderNameFocused
                ? 'text-sm top-0.5 left-3 px-1'
                : 'text-base top-1/2 left-4 -translate-y-1/2',
            )}
            htmlFor="cardholderName"
          >
            {t('upgradePayment.cardholderName')}
          </label>
          <input
            className="w-full text-base focus:outline-none focus:ring-0 focus:border-transparent pt-4 text-black"
            id="cardholderName"
            type="text"
            value={cardholderName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCardholderName(e.target.value)}
            onFocus={() => setIsCardholderNameFocused(true)}
            onBlur={() => setIsCardholderNameFocused(false)}
            required
          />
        </div>
      </div>

      <div className="text-sm text-secondary flex items-center gap-2">
        <Checkbox
          checked={termsAccepted}
          onCheckedChange={(checked) => onTermsAcceptedChange(checked === true)}
        />
        <div>
          {t('paymentForm.terms.text')}{' '}
          <Link
            className="text-highlighted cursor-pointer underline"
            to="https://www.welya.io/cgu"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('paymentForm.terms.link')}
          </Link>
        </div>
      </div>

      {setupOnly && (
        <p className="text-sm text-secondary py-2 flex items-center gap-2">
          <ShieldCheck className="size-6 text-highlighted" />
          {t('paymentForm.shield')}
        </p>
      )}

      <div className="flex gap-4 ">
        <Button
          type="button"
          variant="outline"
          disabled={isProcessing}
          className="w-1/2"
          onClick={handleReset}
        >
          {t('paymentForm.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={
            disabled ||
            !stripe ||
            isProcessing ||
            !isFormValid ||
            !termsAccepted ||
            !workspaceId ||
            (!setupOnly && !priceId)
          }
          className="w-1/2"
        >
          {isProcessing && <Loader />}
          {isProcessing
            ? t('paymentForm.processing')
            : setupOnly
              ? t('paymentForm.setupAndContinue')
              : amount != null
                ? t('paymentForm.pay', { amount: formatPrice(amount, currency) })
                : t('paymentForm.paySimple')}
        </Button>
      </div>
    </form>
  );
}
